import { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import Tooltip from '@mui/material/Tooltip';
import { MissingAsrMetricsCard } from 'pages/ArticleVisibility/components/MissingAsrMetricsCard/MissingAsrMetricsCard';
import { useAppStore } from 'hooks/state/useAppStore';
import { useTransitiveCacheStore } from 'hooks/state/useTransitiveCacheStore';
import { LoadingSpinner } from 'components/LoadingSpinner';
import { MetricsCard } from '../MetricsCard/MetricsCard';
import { MetricsCardContent } from '../../model';
import { ArticleVisibilityInfo, ArticleVisibilityInfoButton } from '../ArticleVisibilityInfo/ArticleVisibilityInfo';

interface MetricsSectionProps {
  content: Record<string, MetricsCardContent[]>;
  hasOnlineMarketplace: boolean;
  marketplacesWithoutOnline: string[];
  isOnlinePhaseLoading: boolean;
}

export function MetricsSection({
  content,
  hasOnlineMarketplace,
  marketplacesWithoutOnline,
  isOnlinePhaseLoading,
}: MetricsSectionProps) {
  const intl = useIntl();

  const { allMarketplaces } = useTransitiveCacheStore();
  const { marketplaces: currentSelectedMarketplaces, setMarketplaces } = useAppStore();
  const canDeselectUnavailableMarketplaces =
    (currentSelectedMarketplaces?.length ?? 0) > 0 || allMarketplaces.length > 0;

  const deselectUnavailableMarketplaces = () => {
    if (!canDeselectUnavailableMarketplaces) {
      return;
    }

    const marketplacesToFilter =
      currentSelectedMarketplaces && currentSelectedMarketplaces.length > 0
        ? currentSelectedMarketplaces
        : allMarketplaces.map(marketplace => marketplace.name);

    const updatedMarketplaces = marketplacesToFilter.filter(
      marketplace => !marketplacesWithoutOnline.includes(marketplace)
    );

    setMarketplaces(updatedMarketplaces);
  };

  const missingAsrMetricCard = (
    <MissingAsrMetricsCard
      marketplacesWithoutOnline={marketplacesWithoutOnline}
      onClickDeselectUnavailable={deselectUnavailableMarketplaces}
      disabled={!canDeselectUnavailableMarketplaces}
    />
  );

  const loadingAsrMetricCard = (
    <div className="flex min-h-[320px] items-center justify-center rounded-lg border-1 border-[#E6E6E6] bg-surface-grey p-6">
      <LoadingSpinner />
    </div>
  );

  const isOnlineColumn = (steps: MetricsCardContent[]) => steps[0]?.column === 'online';

  const createColumnContent = (steps: MetricsCardContent[]): ReactNode =>
    isOnlineColumn(steps) && isOnlinePhaseLoading
      ? loadingAsrMetricCard
      : isOnlineColumn(steps) && !hasOnlineMarketplace
        ? missingAsrMetricCard
        : steps.map((stepContent: MetricsCardContent) => (
            <MetricsCard key={stepContent.cardId} content={stepContent} />
          ));

  return (
    <div className="grid grid-cols-5 gap-3 justify-center w-full">
      {/* Header row */}
      <div className="col-span-5 grid grid-cols-5 gap-3">
        {Object.entries(content).map(([label, steps]) => {
          const subTitleId = `${steps[0].stepperId}.drawer.subTitle`;

          return (
            <div
              key={`header-${label}`}
              className="flex justify-between bg-surface-default w-full items-center  px-2 rounded-lg"
            >
              <Tooltip
                arrow
                title={
                  <div>
                    <strong>
                      {steps[0].stepperId &&
                        intl.formatMessage({
                          id: `${steps[0].stepperId}.label`,
                          defaultMessage: steps[0].label,
                        })}
                    </strong>
                    <br />
                    {intl.formatMessage({
                      id: `${steps[0].stepperId}.tooltip`,
                      defaultMessage: steps[0].label,
                    })}
                  </div>
                }
                disableInteractive
              >
                <p className="text-text-primary line-clamp-1 break-all font-bold leading-tight hover:cursor-default">
                  {steps[0].stepperId &&
                    intl.formatMessage({
                      id: `${steps[0].stepperId}.label`,
                      defaultMessage: steps[0].label,
                    })}
                </p>
              </Tooltip>
              <ArticleVisibilityInfoButton titleKey={`${steps[0].stepperId}.drawer.title`} subtitleKey={subTitleId}>
                <ArticleVisibilityInfo
                  titleTranslation="articleVisibility.cards"
                  subTitleTranslation={steps[0].stepperId}
                />
              </ArticleVisibilityInfoButton>
            </div>
          );
        })}
      </div>
      {/* Content rows - each column independent */}
      {Object.entries(content).map(([label, steps]) => (
        <div key={`content-${label}`} className="flex flex-col gap-3">
          {createColumnContent(steps)}
        </div>
      ))}
    </div>
  );
}
